import { Box } from '@mui/material';
import {
  Button,
  FormErrors,
  Modal,
  ModalProps,
  TextInput,
} from 'components/ui';
import { UpdateZipCodeForm, updateZipCodeFormSchema } from 'forms';
import { useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useState } from 'react';
import { useProfileApi } from 'services/api';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';

export interface UpdateZipCodeModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {}

export const UpdateZipCodeModal = ({
  open,
  onClose,
}: UpdateZipCodeModalProps): ReactElement => {
  const { profile } = useProfile({ revalidateOnMount: true });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields: touched },
  } = useAppForm(updateZipCodeFormSchema);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);

    if (!profile) return;

    reset({
      zipCode: profile.zipCode,
    });
  }, [profile, open]);

  const profileApi = useProfileApi();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = throttle(
    async (data: UpdateZipCodeForm) => {
      setSubmitting(true);
      const result = await profileApi.updateZipCode(data.zipCode);

      if (result.success) return onClose?.();

      setSubmitting(false);
      enqueueSnackbar(result.error.message, { variant: 'error' });
    },
    200,
    { leading: true },
  );

  return (
    <Modal title="Update Your Zip Code" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ width: 600, maxWidth: '80vw' }} display="flex">
          <Box width={300} mr={20}>
            <TextInput
              label="Zip Code"
              defaultValue={profile?.zipCode}
              hasError={touched.zipCode && !!errors.zipCode}
              maxLength={5}
              {...register('zipCode')}
            />
            <FormErrors>{errors.zipCode?.message}</FormErrors>
          </Box>
        </Box>

        <Box mt={30} display="flex">
          <Box mr={30}>
            <Button minWidth={100} inverted onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            minWidth={100}
            type="submit"
            variant="vibrant"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
